<script setup>
/* eslint semi: 1 */

import { ref, onMounted, inject, getCurrentInstance, nextTick, computed } from 'vue';
import { signInWithRedirect as AmplifySignInWithRedirect } from 'aws-amplify/auth';
import { useRoute } from 'vue-router';

import SignInPageContent from '@/components/common/sign-in/sign-in-page-content-tmpl.vue';
import SignUpPageContent from '@/components/common/sign-in/sign-up-page-content-tmpl.vue';
import ForgotPasswordPageContent from '@/components/common/sign-in/forgot-password-page-content-tmpl.vue';
import FormButton from '@/components/common/form/form-button-tmpl.vue';
import AuthSuccess from '@/components/common/sign-in/auth-success-tmpl.vue';
import AuthMixin from '@/components/mixins/auth-mixin';
import EventBus from '@/components/mixins/event-bus-mixin';

import { t } from '@/i18n/i18ns';
import CONST from '@/globals/constants';
import { ssoGetMigrateAccountFlagFromUrl, ssoGetCodeResponseFromUrl } from '@/globals/external-provider-auth-utils';
import { useScreenSize } from '@base-shared/composables/use-screen-size';
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';

defineOptions({
  mixins: [AuthMixin]
});

const AUTH_PAGE_CONTENTS = {
  SIGN_IN: 'sign-in',
  SIGN_UP: 'sign-up',
  FORGOT_PASSWORD: 'forgot_password',
  MIGRATE_TO_SSO_INIT: 'migrate-to-sso-init'
};

const RIGHT_SLOGAN_CONTENT = {
  PRODUCT_SLOGAN: 'product-slogan',
  MIGRATE_TO_SSO_WARNING: 'migrate-to-sso-prompt',
  MIGRATE_TO_SSO_VERIFICATION: 'migrate-to-sso-verification',
  MIGRATE_TO_SSO_CONFIRMATION: 'migrate-to-sso-confirmation',
};

const $utils = inject('$utils');
const $api = inject('$api');
const $apiError = inject('$apiError');
const $router = inject('$routerInstance');
const cognitoAuthStore = useCognitoAuthStore();

const { isPageMini } = useScreenSize();

const signInPageContentRef = ref(null);
const signUpPageContentRef = ref(null);
const forgotPasswordPageContentRef = ref(null);
const currentPageContent = ref(AUTH_PAGE_CONTENTS.SIGN_IN);
const sloganContent = ref(RIGHT_SLOGAN_CONTENT.PRODUCT_SLOGAN);
const ssoData = ref(null);
const isInCodeAndTokenExchange = ref(false);

const productName = computed(() => {
  if ($utils.isPageForLicenses()) {
    return CONST.ACTIVATE;
  }

  return CONFIG.PRODUCT_NAME;
});

const { proxy } = getCurrentInstance();

const route = useRoute();

async function onSignInUserNotConfirmed (signInData) {
  currentPageContent.value = AUTH_PAGE_CONTENTS.SIGN_UP;
  await nextTick();

  if (signInData?.email && signUpPageContentRef.value) {
    signUpPageContentRef.value.resendVerificationCodeRequest(signInData);
  }
}

function onSignUpAutoSignInError (error) {
  // On Signup auto sign in error, we need to redirect to sign in page
  // and show the sign in page content
  console.error('Sign up auto sign in error', error);
  currentPageContent.value = AUTH_PAGE_CONTENTS.SIGN_IN;
}

async function onRedirectToForgotPassword (email) {
  // When user clicks on forgot password, we need to redirect to
  // forgot password page and prefill the email if provided
  currentPageContent.value = AUTH_PAGE_CONTENTS.FORGOT_PASSWORD;
  await nextTick();

  if (email && forgotPasswordPageContentRef.value) {
    forgotPasswordPageContentRef.value.setPrefillEmail(email);
  }
}

// Account Migration to SSO related methods
function onAccountMigrationToSSOInitiated (ssoResponse) {
  sloganContent.value = RIGHT_SLOGAN_CONTENT.MIGRATE_TO_SSO_WARNING;
  currentPageContent.value = AUTH_PAGE_CONTENTS.MIGRATE_TO_SSO_INIT;
  ssoData.value = {
    email: ssoResponse?.email,
    provider: ssoResponse?.provider,
    cognitoStage: ssoResponse?.cognitoStage
  };
}

async function onProceedToSwitchToSSO () {
  currentPageContent.value = AUTH_PAGE_CONTENTS.SIGN_IN;

  const queryParams = { showConfirm: 1, email: ssoData.value.email, provider: ssoData.value.provider, cognitoStage: ssoData.value.cognitoStage, sso: 1 };
  const urlQueryParams = $utils.encodeBase64Format(queryParams);
  sloganContent.value = RIGHT_SLOGAN_CONTENT.MIGRATE_TO_SSO_VERIFICATION;

  // Make sure no logged in user session is present before redirecting to SSO provider for migration
  await proxy.clearAllCognitoDataInLocalstorage();

  await $router.push(`/?sso_migrate_account=${urlQueryParams}`, () => {});
}

async function onConfirmToSwitchToSSO () {
  const ssoMigrateAccount = ssoGetMigrateAccountFlagFromUrl(route.query);
  $utils.mainLoader(true);

  $api.post(`${CONFIG.API_BASE_URL}/sso/${ssoMigrateAccount.provider}`)
    .then(() => {
      // NOTE: Solution for inflight data not being cleared
      window.location.href = `/sso/${ssoMigrateAccount.provider}`;
      $utils.mainLoader(false);
    })
    .catch((error) => {
      $utils.mainLoader(false);
      $apiError.handleAPIRequestError(error, { originalRequest: () => { onConfirmToSwitchToSSO(); }, showDialogNotification: true });
    });
}

function onAccountMigrationToSSOVerified () {
  sloganContent.value = RIGHT_SLOGAN_CONTENT.MIGRATE_TO_SSO_CONFIRMATION;
}

async function handleSSOActions (response) {
  await cognitoAuthStore.fetchAndStoreAuthSession();
  // Read from URl
  if (response.ssoPreSignUpFailed) { // Show Proceed content to migrate account
    const providerAndCognitoStage = cognitoAuthStore.getProviderInfoAndCognitoStage();
    onAccountMigrationToSSOInitiated({
      email: response.ssoPreSignUpFailedEmail,
      provider: providerAndCognitoStage?.SSOProviderName ?? null,
      cognitoStage: providerAndCognitoStage?.CognitoStage ?? null
    });
  } else if (ssoGetMigrateAccountFlagFromUrl(route.query)?.showConfirm && !cognitoAuthStore.isAuthenticated) {
    /**
     * Migrate SSO account.Show SignIn Content
     * If Google/Facebook signIn with showConfirm flag then exchange token first to mark user as signedIn and will show the Confirm content
     */
    const socialAccountSignIn = ['Google', 'Facebook'].includes(response.state?.cognitoIdPName);

    if (socialAccountSignIn) {
      await AmplifySignInWithRedirect({ provider: response.state?.cognitoIdPName });
    }
  } else if (response.error) {
    // Exchange request code with token to signIn. Normal Google/Facebook signIn

    if (response.error) {
      let errorMessage = response.errorDescription ?? '';
      const providerName = cognitoAuthStore.getProviderInfoAndCognitoStage()?.SSOProviderName ?? null;

      if (errorMessage.includes('retry_Google')) {
        await useCognitoAuthStore().configureWithCognitoStage(CONFIG.GLOBAL_STAGE);
        await AmplifySignInWithRedirect({ provider: 'Google' });
        return;
      }

      if (errorMessage.includes('retry_external_linked_user')) {
        $router.push(`/sso/${providerName}`, () => {});
        return;
      }

      if (errorMessage.includes('retry_Facebook')) {
        await useCognitoAuthStore().configureWithCognitoStage(CONFIG.GLOBAL_STAGE);
        await AmplifySignInWithRedirect({ provider: 'Facebook' });
        return;
      }

      if (errorMessage.includes('failed with error No email provided')) {
        errorMessage = t('label.noEmailProvided');
      } else if (errorMessage.includes('User is already linked')) {
        errorMessage = providerName ? t('errors.ssoMigrationFailed') : t('errors.existing.userAlreadyLinked');
      } else if (errorMessage.includes('Not allowed to authenticate via non-SSO Provider')) {
        errorMessage = t('errors.nonSSOAuthDenied');
      } else if (errorMessage.includes('User already has an account in another region')) {
        errorMessage = providerName ? t('errors.cannotMigrateAccountInAnotherRegion') : t('errors.existing.accountInAnotherRegion');
      } else {
        errorMessage = t('errors.loginUnexpectedError');
      }

      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: errorMessage
        },
        callbacks: {
          eventOnClose: async () => {
            // If there's a logged in user session, we want the page to mount again and reconfigure amplify. Otherwise, just redirect to sign in
            await cognitoAuthStore.fetchAndStoreAuthSession();
            if (cognitoAuthStore.isAuthenticated) {
              window.location.href = '/';
            } else {
              $router.push('/');
            }
          }
        }
      });
    }
  }
}

onMounted(() => {
  $router.isReady().then(async () => {
    localStorage.removeItem('aryaSigninAttempt');
    const ssoResponse = ssoGetCodeResponseFromUrl(route.query);
    if (ssoResponse.error) {
      await handleSSOActions(ssoResponse);
    } else if (route.query?.sso_migrate_account) {
      signInPageContentRef.value.commonActionAfterAuthentication();
    } else {
      // Handle redirection to main page and other event emission if user is already authenticated
      proxy.isAuthenticated();
    }
  });
});
</script>

<template>
  <div
    id="signin-page-wrapper"
    class="w-full h-full"
  >
  <div
      v-show="!isInCodeAndTokenExchange"
      id="signin-page"
      class="flex items-center justify-center gap-20 mx-auto p-4 max-w-[1920px]"
    >
      <div
        id="signin"
        class="animated fadeInLeft"
      >
        <!-- SIGN IN -->
        <sign-in-page-content
          v-if="currentPageContent === AUTH_PAGE_CONTENTS.SIGN_IN"
          ref="signInPageContentRef"
          :is-page-mini="isPageMini"
          @redirect-to-sign-up="currentPageContent = AUTH_PAGE_CONTENTS.SIGN_UP"
          @redirect-to-forgot-password="onRedirectToForgotPassword"
          @user-not-confirmed="onSignInUserNotConfirmed"
          @migration-verified="onAccountMigrationToSSOVerified"
        />
        <!-- SIGN UP -->
        <sign-up-page-content
          v-else-if="currentPageContent === AUTH_PAGE_CONTENTS.SIGN_UP"
          ref="signUpPageContentRef"
          :is-page-mini="isPageMini"
          @redirect-to-sign-in="currentPageContent = AUTH_PAGE_CONTENTS.SIGN_IN"
          @auto-sign-in-error="onSignUpAutoSignInError"
        />
        <!-- FORGOT PASSWORD -->
        <forgot-password-page-content
          v-else-if="currentPageContent === AUTH_PAGE_CONTENTS.FORGOT_PASSWORD"
          ref="forgotPasswordPageContentRef"
          :is-page-mini="isPageMini"
          @redirect-to-sign-in="currentPageContent = AUTH_PAGE_CONTENTS.SIGN_IN"
        />

        <div
          v-else-if="currentPageContent === AUTH_PAGE_CONTENTS.MIGRATE_TO_SSO_INIT"
          class="migrate-to-sso-init-card gradient-card default py-6 px-6 flex flex-col items-center max-[1919px]:w-[236px]! max-[1919px]:min-h-[409px]! min-[1920px]:w-[356px]! min-[1920px]:min-h-[629px]!"
          :class="{ 'no-background': $utils.isPageForLicenses() }"
        >
          <h4 class="font-semibold uppercase text-white text-center text-[28px] leading-[100%] p-3">
            {{ $t('label.login') }}
          </h4>
          <auth-success />
        </div>
      </div>

      <!-- Right side slogan -->
      <div
        class="text-start right content-column sign-in-right-column flex flex-col gap-4"
        :class="$utils.isPageForLicenses() ? 'text-white' : 'text-black'"
        data-component-element-id="signin-page-slogan"
      >
        <template v-if="sloganContent === RIGHT_SLOGAN_CONTENT.MIGRATE_TO_SSO_WARNING">
          <h1 class="text-4xl font-semibold">{{'SSO'}}</h1>
          <p class="text-base font-semibold">
            {{ $t('label.ssoMigrateAccountStep1Title1', { email: ssoData.email}) }}
          </p>
          <p class="text-base font-semibold">
            {{ $t('label.ssoMigrateAccountStep1Title2', { provider: ssoData.provider }) }}
          </p>
          <p class="text-base font-semibold">
            {{ $t('label.ssoMigrateAccountStep1Title3') }}
          </p>
          <div class="w-1/3">
            <form-button
              type="button"
              button-theme="dark"
              data-component-element-id="migrate-to-sso-proceed-btn"
              :size="isPageMini ? 'xs' : 'md'"
              :label="$t('label.proceed')"
              @click="onProceedToSwitchToSSO"
            />
          </div>
        </template>
        <template v-else-if="sloganContent === RIGHT_SLOGAN_CONTENT.MIGRATE_TO_SSO_VERIFICATION">
          <h1 class="text-4xl font-semibold">{{'SSO'}}</h1>
          <p class="text-base font-semibold">
            {{ $t('label.ssoMigrateAccountVerifyTitle1', { email: ssoData.email }) }}
          </p>
          <p class="text-base font-semibold">
            {{ $t('label.ssoMigrateAccountVerifyTitle2') }}
          </p>
        </template>
        <template v-else-if="sloganContent === RIGHT_SLOGAN_CONTENT.MIGRATE_TO_SSO_CONFIRMATION">
          <h1 class="text-4xl font-semibold">{{'SSO'}}</h1>
          <p class="text-base font-semibold">
            {{ $t('label.ssoMigrateAccountStep2Title1') }}
          </p>
          <p class="text-base font-semibold">
            {{ $t('label.ssoMigrateAccountStep2Title2') }}
          </p>
          <p class="text-base font-semibold">
            {{ $t('label.ssoMigrateAccountStep2Title3') }}
          </p>
          <div class="w-1/3">
            <form-button
              type="button"
              button-theme="dark"
              data-component-element-id="migrate-to-sso-confirm-btn"
              :size="isPageMini ? 'xs' : 'md'"
              :label="$t('label.confirm')"
              @click="onConfirmToSwitchToSSO"
            />
          </div>
        </template>
        <template v-else>
          <h1 class="text-4xl font-semibold">{{ productName }}</h1>
          <p class="text-base font-semibold">
            <template v-if="$utils.isPageForLicenses()">
              {{ $t('label.loginString4') }}
            </template>
            <template v-else>
              {{ $t('label.loginString3') }}
            </template>
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@reference '#tailwind.scss';

#signin-page-wrapper {
  @media (min-width: 1920px) {
    @media (min-height: 744px) {
      @apply flex items-center justify-center;
    }

    @media (max-height: 743px) {
      @apply overflow-auto;
    }
  }

  @media (max-width: 1919px) {
    @media (min-height: 605px) {
      @apply flex items-center justify-center;
    }

    @media (max-height: 604px) {
      @apply overflow-auto;
    }
  }
}

#signin-page {
  ::v-deep(.sign-in-card.no-background) {
    @apply bg-transparent! border! border-black! box-border!;
    background-size: 0 !important;
  }
}
</style>
